import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { COUNTRIES } from '../../shared/countries';
import { ageFrom, ageRuleFor, judgeAge, type AgeVerdict } from '../../shared/legal';

/**
 * Campos comunes a los dos formularios que piden los mismos datos: el registro
 * y el que completa una cuenta llegada por Google.
 *
 * Están aquí y no duplicados en cada uno porque las reglas de la fecha —rango
 * de años, qué cuenta como completa— tienen que ser las mismas en ambos sitios.
 * Dos copias es una que se queda atrás.
 */

/* ------------------------------------------------------------------ *
 * Aspecto compartido de los campos
 * ------------------------------------------------------------------ */

export const INPUT =
  'w-full h-11 px-3.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm ' +
  'placeholder:text-ink-600 outline-none transition-colors ' +
  'focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25';

/** Igual que INPUT, con hueco para la flecha nativa del desplegable. */
export const SELECT = `${INPUT} pr-9 appearance-none cursor-pointer`;

export const Field: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
  /** El grupo de fecha son tres controles: no puede colgar de un solo `label`. */
  asFieldset?: boolean;
}> = ({ label, hint, children, asFieldset }) => {
  const Etiqueta = asFieldset ? 'legend' : 'span';
  const Contenedor = asFieldset ? 'fieldset' : 'label';
  return (
    <Contenedor className="block">
      <Etiqueta className="block text-[0.8125rem] font-medium text-ink-300 mb-1.5">
        {label}
      </Etiqueta>
      {children}
      {hint && <span className="block text-[0.6875rem] text-ink-500 mt-1.5">{hint}</span>}
    </Contenedor>
  );
};

/** Flecha propia: la nativa no se puede teñir y desentona con el tema. */
const Chevron: React.FC = () => (
  <svg
    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-500"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ------------------------------------------------------------------ *
 * Fecha de nacimiento
 * ------------------------------------------------------------------ */

export interface BirthDateValue {
  day: string;
  month: string;
  year: string;
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/** Coincide con MAX_AGE del servidor. */
const AÑOS_ATRAS = 120;

export function isBirthDateReady(v: BirthDateValue): boolean {
  return Boolean(v.day && v.month && v.year);
}

/**
 * Tres desplegables en lugar de `<input type="date">`.
 *
 * El selector nativo se abre en el mes actual, y llegar desde ahí a 1994 son
 * decenas de toques en un móvil. Además cada navegador lo dibuja a su manera,
 * así que dentro de un modal con tema propio siempre desentona uno.
 */
export const BirthDateFields: React.FC<{
  value: BirthDateValue;
  onChange: (value: BirthDateValue) => void;
}> = ({ value, onChange }) => {
  const añoActual = new Date().getFullYear();
  const años = useMemo(
    () => Array.from({ length: AÑOS_ATRAS + 1 }, (_, i) => añoActual - i),
    [añoActual],
  );

  // Días reales del mes elegido: sin esto se puede componer un 31 de febrero y
  // el error solo aparecería al enviar.
  const diasDelMes = useMemo(() => {
    const mes = Number(value.month);
    const año = Number(value.year);
    if (!mes) return 31;
    if (!año) return mes === 2 ? 29 : [4, 6, 9, 11].includes(mes) ? 30 : 31;
    return new Date(año, mes, 0).getDate();
  }, [value.month, value.year]);

  const set = (patch: Partial<BirthDateValue>) => {
    const next = { ...value, ...patch };
    // Si cambiar de mes deja el día fuera de rango, se descarta el día en vez
    // de enviar una fecha que no existe.
    if (Number(next.day) > new Date(Number(next.year) || 2000, Number(next.month) || 1, 0).getDate()) {
      next.day = '';
    }
    onChange(next);
  };

  return (
    <Field label="Fecha de nacimiento" asFieldset>
      <div className="grid grid-cols-[5rem_1fr_6rem] gap-2">
        <div className="relative">
          <select
            value={value.day}
            onChange={(e) => set({ day: e.target.value })}
            required
            aria-label="Día"
            className={SELECT}
          >
            <option value="">Día</option>
            {Array.from({ length: diasDelMes }, (_, i) => i + 1).map((d) => (
              <option key={d} value={String(d).padStart(2, '0')}>
                {d}
              </option>
            ))}
          </select>
          <Chevron />
        </div>

        <div className="relative">
          <select
            value={value.month}
            onChange={(e) => set({ month: e.target.value })}
            required
            aria-label="Mes"
            className={SELECT}
          >
            <option value="">Mes</option>
            {MESES.map((nombre, i) => (
              <option key={nombre} value={String(i + 1).padStart(2, '0')}>
                {nombre}
              </option>
            ))}
          </select>
          <Chevron />
        </div>

        <div className="relative">
          <select
            value={value.year}
            onChange={(e) => set({ year: e.target.value })}
            required
            aria-label="Año"
            className={SELECT}
          >
            <option value="">Año</option>
            {años.map((a) => (
              <option key={a} value={String(a)}>
                {a}
              </option>
            ))}
          </select>
          <Chevron />
        </div>
      </div>
    </Field>
  );
};

/* ------------------------------------------------------------------ *
 * Edad y consentimiento
 * ------------------------------------------------------------------ */

export interface ConsentValue {
  acceptTerms: boolean;
  guardianConsent: boolean;
}

/**
 * Qué pide la ley para esa fecha y ese país, calculado aquí con las mismas
 * reglas que aplicará el servidor (shared/legal.ts). Sirve para enseñar la
 * casilla del tutor y avisar de la edad ANTES de enviar, no para decidir: la
 * última palabra la tiene el servidor.
 */
export function ageVerdict(birth: BirthDateValue, country: string): AgeVerdict | null {
  if (!isBirthDateReady(birth) || !country) return null;
  return judgeAge(ageFrom(`${birth.year}-${birth.month}-${birth.day}`), ageRuleFor(country));
}

/** Si el formulario puede enviarse en cuanto a edad y consentimientos. */
export function consentReady(verdict: AgeVerdict | null, value: ConsentValue): boolean {
  if (!verdict?.ok) return false;
  if (verdict.needsGuardian && !value.guardianConsent) return false;
  return value.acceptTerms;
}

/** Enlace a un documento legal, en otra pestaña para no perder el formulario. */
export const LegalLink: React.FC<{ to: 'terminos' | 'privacidad'; children: React.ReactNode }> = ({
  to,
  children,
}) => (
  <a
    href={`/${to}`}
    target="_blank"
    rel="noopener"
    className="text-sky-400 hover:text-sky-300 underline underline-offset-2"
  >
    {children}
  </a>
);

const CHECK = 'mt-0.5 w-4 h-4 shrink-0 accent-ember-500 cursor-pointer';

/**
 * Las casillas de aceptación, con el aviso de edad cuando toca.
 *
 * Casillas sin marcar y obligatorias: un "al registrarte aceptas" en letra
 * pequeña no vale como consentimiento en la UE, y es lo primero que mira
 * quien revise si alguien aceptó de verdad.
 */
export const ConsentFields: React.FC<{
  verdict: AgeVerdict | null;
  value: ConsentValue;
  onChange: (value: ConsentValue) => void;
}> = ({ verdict, value, onChange }) => {
  if (verdict && !verdict.ok) {
    return (
      <p
        role="alert"
        className="flex items-start gap-2 rounded-lg border border-ember-400/30 bg-ember-400/10 px-3 py-2.5 text-ember-300 text-[0.8125rem]"
      >
        <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
        <span>
          En tu país hace falta tener al menos {verdict.min} años para crear una cuenta. Puedes
          seguir jugando como invitado con el PIN de una partida, sin registrarte.
        </span>
      </p>
    );
  }

  return (
    <div className="space-y-2.5">
      {verdict?.ok && verdict.needsGuardian && (
        <label className="flex items-start gap-2.5 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2.5 text-[0.8125rem] text-ink-200 cursor-pointer">
          <input
            type="checkbox"
            checked={value.guardianConsent}
            onChange={(e) => onChange({ ...value, guardianConsent: e.target.checked })}
            className={CHECK}
          />
          <span>
            <strong className="text-ink-50">Para la madre, el padre o el tutor legal:</strong> esta
            cuenta es de un menor de {verdict.alone} años. Declaro que soy su madre, padre o tutor
            legal, que he leído los <LegalLink to="terminos">Términos</LegalLink> y la{' '}
            <LegalLink to="privacidad">Política de privacidad</LegalLink>, y que doy mi permiso.
          </span>
        </label>
      )}

      <label className="flex items-start gap-2.5 text-[0.8125rem] text-ink-300 cursor-pointer">
        <input
          type="checkbox"
          checked={value.acceptTerms}
          onChange={(e) => onChange({ ...value, acceptTerms: e.target.checked })}
          className={CHECK}
        />
        <span>
          He leído y acepto los <LegalLink to="terminos">Términos de uso</LegalLink> y la{' '}
          <LegalLink to="privacidad">Política de privacidad</LegalLink>.
        </span>
      </label>
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * País
 * ------------------------------------------------------------------ */

export const CountryField: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => (
  <Field label="País">
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className={SELECT}
      >
        <option value="">Selecciona tu país</option>
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>
            {c.name}
          </option>
        ))}
      </select>
      <Chevron />
    </div>
  </Field>
);
